import React, { useState, useEffect } from 'react';
import { useWorksheets } from '../../hooks/useWorksheets';
import { useAuth } from '../../contexts/AuthContext';
import { WorksheetTemplate, WorksheetItem, WorksheetTask, ITEM_TYPES, calculateItemTotal } from '../../types/worksheet';
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

interface WorksheetTemplateManagerProps {
  triggerNewForm?: boolean;
  onFormOpened?: () => void;
}

const WorksheetTemplateManager: React.FC<WorksheetTemplateManagerProps> = ({ triggerNewForm, onFormOpened }) => {
  const { t } = useLanguage();
  const { templates, templatesLoading, loadTemplates, createTemplate, deleteTemplate } = useWorksheets();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [estimatedHours, setEstimatedHours] = useState<number | ''>('');
  const [defaultWorkType, setDefaultWorkType] = useState<'installation' | 'maintenance' | 'repair' | 'inspection' | 'other'>('other');
  const [defaultPriority, setDefaultPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [defaultItems, setDefaultItems] = useState<Omit<WorksheetItem, 'id' | 'totalNet' | 'totalGross'>[]>([]);
  const [defaultTasks, setDefaultTasks] = useState<Omit<WorksheetTask, 'id'>[]>([]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error(t('worksheets.templates.nameRequired'));
      return;
    }
    if (!user?.organizationId) {
      toast.error(t('worksheets.templates.noOrganization'));
      return;
    }
    await createTemplate({
      name: name.trim(),
      description: description.trim(),
      category,
      estimatedHours: estimatedHours || undefined,
      defaultWorkType,
      defaultPriority,
      defaultItems,
      defaultTasks: defaultTasks.map((t, idx) => ({ ...t, id: `task_${Date.now()}_${idx}`, order: idx })),
      organizationId: user.organizationId,
      isActive: true,
      createdBy: user.uid,
    });
    setName('');
    setDescription('');
    setEstimatedHours('');
    setDefaultItems([]);
    setDefaultTasks([]);
    setIsModalOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('worksheets.templates.confirmDelete'))) {
      await deleteTemplate(id);
    }
  };

  const addItem = () => {
    setDefaultItems([...defaultItems, {
      type: 'labor',
      name: '',
      quantity: 1,
      unit: 'ó',
      unitPrice: 0,
      vatRate: 27,
      order: defaultItems.length,
    }]);
  };

  const addTask = () => {
    setDefaultTasks([...defaultTasks, {
      title: '',
      isCompleted: false,
      order: defaultTasks.length,
    }]);
  };

  useEffect(() => {
    if (triggerNewForm) {
      setIsModalOpen(true);
      onFormOpened?.();
    }
  }, [triggerNewForm, onFormOpened]);

  return (
    <div className="space-y-4">
      {templatesLoading && <p className="text-sm text-gray-400">{t('worksheets.templates.loading')}</p>}

      {!templatesLoading && templates.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-400">
          {t('worksheets.templates.empty')}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map((tpl) => (
          <div key={tpl.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{tpl.name}</h3>
                {tpl.description && <p className="text-xs text-gray-500 mt-1">{tpl.description}</p>}
              </div>
              <button
                onClick={() => handleDelete(tpl.id)}
                className="p-1 text-red-400 hover:text-red-600"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-3 flex items-center space-x-2 text-xs text-gray-500">
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{tpl.category}</span>
              {tpl.estimatedHours && <span>~{tpl.estimatedHours}ó</span>}
              <span>{tpl.defaultItems.length} {t('worksheets.templates.itemsCount')}</span>
              <span>{tpl.defaultTasks.length} {t('worksheets.templates.tasksCount')}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={t('worksheets.templates.new')} size="lg">
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('worksheets.templates.name')} *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('worksheets.templates.description')}</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('worksheets.templates.category')}</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                <option value="installation">{t('worksheets.templates.categoryName.installation')}</option>
                <option value="maintenance">{t('worksheets.templates.categoryName.maintenance')}</option>
                <option value="repair">{t('worksheets.templates.categoryName.repair')}</option>
                <option value="inspection">{t('worksheets.templates.categoryName.inspection')}</option>
                <option value="other">{t('worksheets.templates.categoryName.other')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('worksheets.templates.estimatedHours')}</label>
              <input
                type="number"
                value={estimatedHours}
                onChange={e => setEstimatedHours(e.target.value ? parseFloat(e.target.value) : '')}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('worksheets.templates.priority')}</label>
              <select
                value={defaultPriority}
                onChange={e => setDefaultPriority(e.target.value as any)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                <option value="low">{t('worksheets.priority.low')}</option>
                <option value="medium">{t('worksheets.priority.medium')}</option>
                <option value="high">{t('worksheets.priority.high')}</option>
              </select>
            </div>
          </div>

          {/* Default items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('worksheets.templates.defaultItems')}</label>
              <button onClick={addItem} className="text-xs text-blue-500 hover:text-blue-600">
                {t('worksheets.templates.addItem')}
              </button>
            </div>
            {defaultItems.map((item, idx) => (
              <div key={idx} className="flex items-center space-x-2 mb-1">
                <select
                  value={item.type}
                  onChange={e => {
                    const updated = [...defaultItems];
                    updated[idx] = { ...item, type: e.target.value as any };
                    setDefaultItems(updated);
                  }}
                  className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-1 bg-white dark:bg-gray-800"
                >
                  {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input
                  type="text"
                  value={item.name}
                  onChange={e => {
                    const updated = [...defaultItems];
                    updated[idx] = { ...item, name: e.target.value };
                    setDefaultItems(updated);
                  }}
                  placeholder={t('worksheets.items.name')}
                  className="flex-1 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800"
                />
                <input
                  type="number"
                  value={item.quantity}
                  onChange={e => {
                    const updated = [...defaultItems];
                    updated[idx] = { ...item, quantity: parseFloat(e.target.value) || 0 };
                    setDefaultItems(updated);
                  }}
                  className="w-16 text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-1 bg-white dark:bg-gray-800"
                />
                <input
                  type="number"
                  value={item.unitPrice}
                  onChange={e => {
                    const updated = [...defaultItems];
                    updated[idx] = { ...item, unitPrice: parseFloat(e.target.value) || 0 };
                    setDefaultItems(updated);
                  }}
                  className="w-20 text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-1 bg-white dark:bg-gray-800"
                />
                <button
                  onClick={() => setDefaultItems(defaultItems.filter((_, i) => i !== idx))}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <TrashIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* Default tasks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('worksheets.templates.defaultTasks')}</label>
              <button onClick={addTask} className="text-xs text-blue-500 hover:text-blue-600">
                {t('worksheets.templates.addTask')}
              </button>
            </div>
            {defaultTasks.map((task, idx) => (
              <div key={idx} className="flex items-center space-x-2 mb-1">
                <input
                  type="text"
                  value={task.title}
                  onChange={e => {
                    const updated = [...defaultTasks];
                    updated[idx] = { ...task, title: e.target.value };
                    setDefaultTasks(updated);
                  }}
                  placeholder={t('worksheets.templates.taskName')}
                  className="flex-1 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800"
                />
                <input
                  type="number"
                  value={task.estimatedMinutes || ''}
                  onChange={e => {
                    const updated = [...defaultTasks];
                    updated[idx] = { ...task, estimatedMinutes: parseInt(e.target.value) || undefined };
                    setDefaultTasks(updated);
                  }}
                  placeholder={t('worksheets.templates.minutes')}
                  className="w-16 text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-1 bg-white dark:bg-gray-800"
                />
                <button
                  onClick={() => setDefaultTasks(defaultTasks.filter((_, i) => i !== idx))}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <TrashIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {t('worksheets.templates.cancel')}
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600"
            >
              {t('worksheets.templates.create')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WorksheetTemplateManager;
